import React, { useEffect, useState, useContext } from "react";
import { Layout } from "@shopify/polaris";
import { useLazyQuery } from "@apollo/react-hooks";
import "jspdf-autotable";

// import hoc
import { withFeature, withErrorBoundary } from "lib/hoc";

import constant from "lib/constant/constant";
import { baseHelper, fileHelper, storageHelper } from "lib/helpers";
import { PrivateContext } from "lib/context";
import { Banner, Spinner } from "lib/components";

import { GET_ORDER_LINES, GET_ORDER_EXPORT_SETTING } from "app/orders/apollo/queries";
import { GET_WHOLESALE_SETTING } from "app/product/apollo/queries";

import orderHelper from "app/orders/modules/orderHelper";
// import { constants } from "crypto";

const ExportOrderAsPDF = () => {
  const data = useContext(PrivateContext);
  const brandLogo = storageHelper.get("logoUrl");
  const defaultLogo = constant.DEFAULT_PDF_LOGO;
  const { cms, currentUser } = data;
  const isVendor = baseHelper.isVendor(currentUser);
  const [isPageLoading, setIsPageLoading] = useState(true);
  const [queryData, setQueryData] = useState();
  const [
    getOrderLines,
    { data: getOrderData, loading: getOrderLoading, error: getOrderError },
  ] = useLazyQuery(GET_ORDER_LINES, { variables: { input: queryData } });

  const [
    getOrderSetting,
    // eslint-disable-next-line no-unused-vars
    { data: getOrderSettingData, loading: getPaymentSettingLoading, error: getPaymentSettingError },
  ] = useLazyQuery(GET_ORDER_EXPORT_SETTING);

  const [getWholesaleSetting, { loading: wholesaleLoading, data: wholesaleData }] = useLazyQuery(GET_WHOLESALE_SETTING);

  const [banner, setBanner] = useState({
    title: "",
    status: "",
    isOpen: false,
  });

  useEffect(() => {
    if (!getOrderLoading && !getOrderError && getOrderData && getOrderSettingData && !wholesaleLoading) {
      const resData = baseHelper.getResponseData(getOrderData, "getOrderLines");
      const settingResponse = baseHelper.getResponseData(getOrderSettingData, constant.gql.GET_ORDER_EXPORT_SETTING);
      let bannerData = false;
      if (!resData) {
        const resError = baseHelper.getResponseError(getOrderData, "getOrderLines");
        bannerData = {
          title: resError,
          status: constant.CRITICAL,
          isOpen: true,
        };
      }
      if (!resData.length) {
        bannerData = {
          title: cms("common.message.error.noOrder"),
          status: constant.CRITICAL,
          isOpen: true,
        };
      }
      if (bannerData) {
        setBanner(bannerData);
        setTimeout(() => {
          window.close();
        }, 2000);
        return;
      }
      const wholesaleResponse = baseHelper.getResponseData(wholesaleData, constant.gql.GET_WHOLESALE_SETTING);
      const { isWholesalePriceVisible = false } = wholesaleResponse;
      const pdfData = orderHelper.orderCSVData(
        resData,
        settingResponse,
        isWholesalePriceVisible,
        false,
        true,
        isVendor,
        cms
      );
      if (brandLogo) {
        fileHelper
          .loadImage(brandLogo)
          .then((logo) => {
            fileHelper.generatePdf(logo, pdfData, constant.ORDER);
          })
          .catch(() => {
            fileHelper.generatePdf(false, pdfData, constant.ORDER);
          });
      } else {
        fileHelper
          .loadImage(defaultLogo)
          .then((logo) => {
            fileHelper.generatePdf(logo, pdfData, constant.ORDER);
          })
          .catch(() => {
            fileHelper.generatePdf(false, pdfData, constant.ORDER);
          });
      }
    }
  }, [
    getOrderData,
    getOrderError,
    getOrderLoading,
    cms,
    getOrderSettingData,
    data,
    wholesaleLoading,
    wholesaleData,
    isVendor,
    currentUser.logo,
  ]);

  useEffect(() => {
    const exportOrder = () => {
      const dataToFetch = {};
      const { status, fulfillmentStatus, vendor, accountingStatus } = baseHelper.queryParamsFromLocation(data);
      if (status && status.length) {
        dataToFetch.status = status.split(",");
      }
      if (fulfillmentStatus && fulfillmentStatus.length) {
        dataToFetch.fulfillmentStatus = fulfillmentStatus.split(",");
      }
      if (vendor && vendor.length) {
        dataToFetch.vendor = vendor.split(",");
      }
      if (accountingStatus && accountingStatus.length) {
        dataToFetch.accountingStatus = accountingStatus.split(",");
      }
      setQueryData(dataToFetch);
      getWholesaleSetting();
      getOrderLines();
      getOrderSetting();
    };
    if (isPageLoading) {
      exportOrder();
      setIsPageLoading(false);
    }
  }, [data, getOrderLines, getOrderSetting, getWholesaleSetting, isPageLoading]);

  return (
    <Layout>
      {getOrderLoading && <Spinner isFullPage />}
      <Layout.Section>
        {banner.isOpen && (
          <Banner
            title={banner.title}
            status={banner.status}
            isOpen={banner.isOpen}
            onDismiss={() => setBanner({ isOpen: false, status: "", title: "" })}
          />
        )}
      </Layout.Section>
    </Layout>
  );
};

export default withFeature(withErrorBoundary(ExportOrderAsPDF), { feature: constant.EXPORT_ORDER });
